import React, { useMemo, useState } from "react";
import { Box, IconButton, Popover, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { Clear } from "@mui/icons-material";
import { ChromePicker } from "react-color";

/**
 * Curate one catalog display colour.
 *
 * Both levels of the catalog carry a colour and both are edited the same way,
 * so the swatch/hex/picker trio lives here rather than twice in the dialogs.
 *
 * Three ways in, because admins arrive with different things in hand: the
 * palette of colours already in use (the common case — a new term should look
 * like its neighbours), a hex code pasted from a design note, and the picker
 * for when someone just wants to choose. "No colour" is a first-class value:
 * uncoloured rows are omitted from GetTherapyColors and the planning surfaces
 * fall back on their own default, so clearing must stay one click away.
 */

/** Mirrors HEX_COLOR_PATTERN in the API's catalog-admin.dto.ts. */
const HEX = /^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

export const isValidHexColor = (value: string): boolean => HEX.test(value.trim());

/**
 * Read-only swatch for the admin tables. An uncoloured row shows a dash rather
 * than an empty cell, so "nothing curated here" is legible at a glance — that
 * is the thing an admin scans the column for.
 */
export const ColorSwatch: React.FC<{ color?: string | null }> = ({ color }) => {
  if (!color || !isValidHexColor(color)) {
    return (
      <Typography variant="caption" color="text.disabled">
        —
      </Typography>
    );
  }
  return (
    <Tooltip title={color}>
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          display: "inline-block",
          verticalAlign: "middle",
          backgroundColor: color,
          border: "1px solid",
          borderColor: "divider",
        }}
      />
    </Tooltip>
  );
};

interface Props {
  /** Current colour, or "" / null for "no colour curated". */
  value: string | null;
  onChange: (value: string | null) => void;
  label?: string;
  /** Colours already used elsewhere in the catalog, offered as a quick palette. */
  suggestions?: string[];
  helperText?: string;
}

const ColorField: React.FC<Props> = ({
  value,
  onChange,
  label = "Farbe",
  suggestions = [],
  helperText,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const current = value ?? "";
  // An empty field is valid — it means "no colour", not "bad input".
  const invalid = current.trim().length > 0 && !isValidHexColor(current);

  const palette = useMemo(() => {
    const seen = new Set<string>();
    for (const c of suggestions) {
      if (c && isValidHexColor(c)) seen.add(c.toLowerCase());
    }
    return [...seen].sort();
  }, [suggestions]);

  // The server lowercases what it stores, so doing the same here keeps the
  // palette's "currently selected" highlight from missing on a pasted #4CAF50.
  const commit = (hex: string | null) => onChange(hex ? hex.toLowerCase() : null);

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>

      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Tooltip title={current ? "Farbe wählen" : "Keine Farbe — zum Wählen klicken"}>
          <Box
            onClick={(e) => setAnchor(e.currentTarget)}
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 1,
              cursor: "pointer",
              border: "2px solid",
              borderColor: "divider",
              backgroundColor: invalid || !current ? "transparent" : current,
              // The unset state has to read as "nothing chosen" rather than
              // "chosen white", hence the diagonal strike.
              backgroundImage: current
                ? undefined
                : "linear-gradient(135deg, transparent 46%, rgba(0,0,0,0.35) 46%, rgba(0,0,0,0.35) 54%, transparent 54%)",
              "&:hover": { borderColor: "primary.main" },
            }}
          />
        </Tooltip>

        <TextField
          size="small"
          value={current}
          onChange={(e) => {
            const next = e.target.value.trim();
            onChange(next ? next : null);
          }}
          placeholder="#4caf50"
          error={invalid}
          helperText={invalid ? "Kein gültiger Hex-Wert, z. B. #4caf50" : helperText}
          sx={{ width: 200 }}
          inputProps={{ style: { fontFamily: "monospace" } }}
        />

        <Tooltip title="Farbe entfernen">
          <span>
            <IconButton size="small" disabled={!current} onClick={() => commit(null)}>
              <Clear fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        {palette.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ pt: 0.5 }}>
            {palette.map((c) => (
              <Tooltip key={c} title={c}>
                <Box
                  onClick={() => commit(c)}
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    cursor: "pointer",
                    backgroundColor: c,
                    border: current.toLowerCase() === c ? "3px solid" : "1px solid",
                    borderColor: current.toLowerCase() === c ? "primary.main" : "divider",
                  }}
                />
              </Tooltip>
            ))}
          </Stack>
        )}
      </Stack>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <ChromePicker
          color={isValidHexColor(current) ? current : "#cccccc"}
          disableAlpha
          onChange={(c) => commit(c.hex)}
        />
      </Popover>
    </Box>
  );
};

export default ColorField;
